/**
 * Fotos ohne Projekt: hochladen → Bericht → zuordnen.
 *
 * Benjamin, 13.10.2026: „Die Bilder sollen erst einfach hochgeladen werden. Der
 * Fortschritt muss zu sehen sein. Nach dem Upload ein Bericht — wie viele Bilder,
 * Projekte automatisch erkannt … Und dann muss es einfach sein, die restlichen
 * Bilder zuzuordnen. Alle Projekte unabhängig vom Status.“ Dazu eine Karte, auf der
 * Projekte gewählt werden können, und eine Suche.
 *
 * Sortiert wird auf dem Server (`foto_eingang`): 150 m um die Baustelle, Phase aus
 * den Terminen; ohne Aufnahmeort oder doppelt → abgelehnt; unklar → Stapel.
 */
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { fehlerText } from "@vimaana/ui";
import Arbeitsfortschritt from "../components/fotos/Arbeitsfortschritt";
import FotoKarte from "../components/fotos/FotoKarte";
import FotoUebersicht from "../components/fotos/FotoUebersicht";
import { PHASEN, PHASE_TEXT, kurzTag, phaseVorschlag } from "../components/fotos/phase";
import ProjektSuche, { entfernung, meter } from "../components/fotos/ProjektSuche";
import {
  useFotoStapel, useStapelVerwerfen, useStapelZuordnen, useZuordnenProjekte,
  type EingangAbgelehnt, type FotoPhase, type Schritt, type StapelBuendel, type ZuordnenProjekt,
} from "../hooks/useMedien";
import { mb, restzeit, useFotoHochladen, type Lauf } from "../lib/fotoHochladen";
import "../styles/projekte.css";
import "../styles/fotos.css";

const BILDTYPEN = "image/jpeg,image/png,image/webp,image/heic,image/heif";
const BILD_ENDUNG = /\.(jpe?g|png|webp|heic|heif)$/i;

function nurFotos(liste: FileList | File[] | null | undefined): { fotos: File[]; andere: number } {
  const alle = Array.from(liste ?? []);
  const fotos = alle.filter((f) => f.type.startsWith("image/") || BILD_ENDUNG.test(f.name));
  return { fotos, andere: alle.length - fotos.length };
}

const ABGELEHNT_TITEL: Record<EingangAbgelehnt["art"], string> = {
  doppelt: "schon vorhanden — nicht noch einmal gesendet",
  ohne_ort: "ohne Aufnahmeort — nicht gespeichert",
  fehler: "nicht gespeichert",
};

function tagLabel(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" });
}

export default function FotoEingangPage() {
  const { lauf, hinzufuegen, abbrechen } = useFotoHochladen();
  const stapel = useFotoStapel();
  const projekte = useZuordnenProjekte();
  const [ziehen, setZiehen] = useState(false);
  const [uebersprungen, setUebersprungen] = useState(0);
  const feld = useRef<HTMLInputElement>(null);
  const zuordnenRef = useRef<HTMLElement>(null);

  function dazu(liste: FileList | File[] | null | undefined) {
    const { fotos, andere } = nurFotos(liste);
    setUebersprungen(andere);
    if (fotos.length) void hinzufuegen(fotos);
  }

  // Ablegen überall auf der Seite — sonst öffnet der Browser ein danebengefallenes Bild.
  const dazuRef = useRef(dazu);
  dazuRef.current = dazu;
  useEffect(() => {
    let tiefe = 0;
    const mitDateien = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");
    const rein = (e: DragEvent) => { if (!mitDateien(e)) return; e.preventDefault(); tiefe += 1; setZiehen(true); };
    const ueber = (e: DragEvent) => { if (!mitDateien(e)) return; e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = "copy"; };
    const raus = (e: DragEvent) => { if (!mitDateien(e)) return; tiefe = Math.max(0, tiefe - 1); if (!tiefe) setZiehen(false); };
    const fallen = (e: DragEvent) => {
      if (!mitDateien(e)) return;
      e.preventDefault(); tiefe = 0; setZiehen(false);
      dazuRef.current(e.dataTransfer?.files);
    };
    window.addEventListener("dragenter", rein);
    window.addEventListener("dragover", ueber);
    window.addEventListener("dragleave", raus);
    window.addEventListener("drop", fallen);
    return () => {
      window.removeEventListener("dragenter", rein);
      window.removeEventListener("dragover", ueber);
      window.removeEventListener("dragleave", raus);
      window.removeEventListener("drop", fallen);
    };
  }, []);

  const offen = stapel.data?.anzahl ?? 0;

  return (
    <div className="projekte-page fo-seite">
      <header className="vg-page-head">
        <div className="vg-page-title"><h1>Fotos</h1></div>
      </header>

      <section className="fo-karte" aria-labelledby="fo-hoch">
        <h2 id="fo-hoch">Baustellenfotos hochladen</h2>
        <p className="fo-text">
          Einfach alle Fotos der letzten Tage hierher ziehen — das Hochladen beginnt sofort. Kontor erkennt die
          Baustelle im Umkreis von 150 m und schlägt sie vor; zugeordnet wird erst, wenn du unten bestätigst.
        </p>
        <div
          className={`fo-ablage${ziehen ? " is-ziehen" : ""}${lauf?.laeuft ? " ist-aktiv" : ""}`}
          role="button"
          tabIndex={0}
          onClick={() => feld.current?.click()}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); feld.current?.click(); } }}
        >
          <svg className="fo-ablage-icon" viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor"
            strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 8h3l1.6-2.2h6.8L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
            <circle cx="12" cy="13.3" r="3.4" />
          </svg>
          <strong>{ziehen ? "Loslassen — Hochladen beginnt" : lauf?.laeuft ? "Weitere Fotos dazuziehen" : "Fotos hierher ziehen"}</strong>
          <span>oder klicken, um sie auszuwählen</span>
        </div>
        <input
          ref={feld}
          className="fo-unsichtbar"
          type="file"
          multiple
          accept={BILDTYPEN}
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => { dazu(e.target.files); e.target.value = ""; }}
        />
        {uebersprungen > 0 && (
          <p className="fo-fehler">{uebersprungen === 1 ? "1 Datei ist kein Foto" : `${uebersprungen} Dateien sind keine Fotos`} — übersprungen.</p>
        )}
        {lauf && <Fortschritt lauf={lauf} onAbbrechen={abbrechen} />}
        {lauf && lauf.bericht.fotos > 0 && (
          <Bericht lauf={lauf} offen={offen} onZuordnen={() => zuordnenRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })} />
        )}
        <details className="fo-hilfe">
          <summary>Damit der Aufnahmeort mitkommt</summary>
          <ul>
            <li><strong>Mac:</strong> in der Fotos-App die Bilder markieren, „Ablage → Exportieren → Unverändertes Original
              exportieren“ in einen Ordner, dann aus dem Finder hierher ziehen.</li>
            <li><strong>iPhone:</strong> im Auswahlfenster oben auf „Optionen“ tippen und „Ort“ einschalten.</li>
            <li><strong>Android:</strong> der Browser entfernt den Ort beim Hochladen — Fotos vom Rechner aus hochladen.</li>
          </ul>
        </details>
      </section>

      <section className="fo-karte" ref={zuordnenRef} aria-labelledby="fo-zu">
        <h2 id="fo-zu">
          Zuordnen
          {offen > 0 && <span className="vg-proj-tab-count">{offen}</span>}
        </h2>
        {stapel.isLoading && <p className="fo-text">Wird geladen …</p>}
        {stapel.isError && <p className="fo-fehler">{fehlerText(stapel.error)}</p>}
        {stapel.data && stapel.data.buendel.length === 0 && (
          <p className="fo-text">Nichts offen — alle Fotos haben ihr Projekt.</p>
        )}
        {stapel.data && stapel.data.buendel.length > 0 && (
          <Zuordnen buendel={stapel.data.buendel} projekte={projekte.data ?? []} projekteLaden={projekte.isLoading} />
        )}
      </section>

      <section className="fo-karte" aria-labelledby="fo-ueb">
        <h2 id="fo-ueb">Zugeordnete Fotos</h2>
        <p className="fo-text">Prüfen, was wo liegt — falsch zugeordnete Fotos antippen und umhängen oder zurück zum Zuordnen legen.</p>
        <FotoUebersicht projekte={projekte.data ?? []} />
      </section>
    </div>
  );
}

function Fortschritt({ lauf, onAbbrechen }: { lauf: Lauf; onAbbrechen: () => void }) {
  // Jede Sekunde neu zeichnen, damit die Restzeit läuft.
  const [, tick] = useState(0);
  useEffect(() => {
    if (!lauf.laeuft) return;
    const t = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, [lauf.laeuft]);
  const anteil = lauf.bytesGesamt > 0 ? Math.min(1, lauf.bytesFertig / lauf.bytesGesamt) : lauf.gesamt ? lauf.fertig / lauf.gesamt : 0;
  const prozent = lauf.laeuft ? Math.round(anteil * 100) : 100;
  const zeit = lauf.laeuft ? restzeit(lauf) : "";
  return (
    <div className={`fo-fortschritt${lauf.laeuft ? "" : " ist-fertig"}`} role="status" aria-live="polite">
      <div className="fo-fortschritt-kopf">
        <strong>
          {lauf.laeuft
            ? lauf.pruefen > 0 && lauf.fertig === 0
              ? `Prüfe ${lauf.gesamt} Fotos …`
              : `${lauf.fertig} von ${lauf.gesamt} Fotos`
            : lauf.abgebrochen
              ? `Abgebrochen — ${lauf.gesamt - lauf.fertig} von ${lauf.gesamt} Fotos nicht gesendet`
              : `Fertig — ${lauf.gesamt} ${lauf.gesamt === 1 ? "Foto" : "Fotos"} verarbeitet`}
        </strong>
        {lauf.laeuft && (
          <span>
            {mb(lauf.bytesFertig)} von {mb(lauf.bytesGesamt)} MB{zeit ? ` · ${zeit}` : ""}
          </span>
        )}
        {lauf.laeuft && <button type="button" className="vg-btn" onClick={onAbbrechen}>Abbrechen</button>}
      </div>
      <div className="fo-balken" aria-hidden="true"><i style={{ width: `${prozent}%` }} /></div>
      {lauf.laeuft && <small className="fo-text">Bitte diesen Tab offen lassen, bis alles hochgeladen ist.</small>}
    </div>
  );
}

function Bericht({ lauf, offen, onZuordnen }: { lauf: Lauf; offen: number; onZuordnen: () => void }) {
  const b = lauf.bericht;
  const erkannt = b.erkannt.reduce((n, z) => n + z.anzahl, 0);
  const nachArt = useMemo(() => {
    const m = new Map<EingangAbgelehnt["art"], EingangAbgelehnt[]>();
    for (const a of b.abgelehnt) m.set(a.art, [...(m.get(a.art) ?? []), a]);
    return [...m.entries()];
  }, [b.abgelehnt]);
  return (
    <div className="fo-bericht">
      <div className="fo-zahlen">
        <div className="fo-zahl"><strong>{b.fotos}</strong><span>{b.fotos === 1 ? "Foto" : "Fotos"} {lauf.laeuft ? "bisher" : "verarbeitet"}</span></div>
        <div className="fo-zahl is-gut">
          <strong>{erkannt}</strong>
          <span>mit Projektvorschlag{b.erkannt.length ? ` · ${b.erkannt.length} ${b.erkannt.length === 1 ? "Projekt" : "Projekte"}` : ""}</span>
        </div>
        <div className={`fo-zahl${b.zuordnen - erkannt > 0 ? " is-offen" : ""}`}>
          <strong>{Math.max(0, b.zuordnen - erkannt)}</strong>
          <span>ohne Vorschlag{offen > b.zuordnen ? ` · ${offen} offen insgesamt` : ""}</span>
        </div>
        <div className={`fo-zahl${b.abgelehnt.length ? " is-warn" : ""}`}><strong>{b.abgelehnt.length}</strong><span>abgelehnt</span></div>
      </div>
      {b.erkannt.length > 0 && (
        <ul className="fo-bericht-projekte">
          {b.erkannt.map((z) => (
            <li key={z.projekt_id}>
              <div className="fo-bericht-zeile">
                <span className="fo-bericht-titel">{z.titel || "Projekt"}</span>
                <span>{z.anzahl} {z.anzahl === 1 ? "Foto" : "Fotos"} vorgeschlagen</span>
              </div>
            </li>
          ))}
        </ul>
      )}
      {nachArt.map(([art, liste]) => (
        <details key={art} className={`fo-abgelehnt is-${art}`}>
          <summary>{liste.length} {ABGELEHNT_TITEL[art]}</summary>
          <ul>{liste.map((a, i) => <li key={`${a.datei}-${i}`}><strong>{a.datei}</strong>: {a.grund}</li>)}</ul>
        </details>
      ))}
      {!lauf.laeuft && offen > 0 && (
        <button type="button" className="vg-btn vg-btn-primary fo-weiter" onClick={onZuordnen}>
          {offen === 1 ? "1 Foto prüfen und zuordnen" : `${offen} Fotos prüfen und zuordnen`} ↓
        </button>
      )}
    </div>
  );
}

type Erledigt = { text: string; projekt_id: string };

function Zuordnen({ buendel, projekte, projekteLaden }: { buendel: StapelBuendel[]; projekte: ZuordnenProjekt[]; projekteLaden: boolean }) {
  const [aktivId, setAktivId] = useState<string | null>(null);
  const [wahl, setWahl] = useState<Record<string, string>>({});
  const [erledigt, setErledigt] = useState<Erledigt[]>([]);
  const aktiv = Math.max(0, buendel.findIndex((b) => b.ids[0] === aktivId));
  const karten = useRef<Record<string, HTMLElement | null>>({});
  // Ohne eigene Wahl gilt der Vorschlag von Kontor — er ist vorausgewählt, nicht zugeordnet.
  const wahlFuer = (b: StapelBuendel | undefined) => (b ? (wahl[b.ids[0]] ?? b.erkannt?.projekt_id ?? null) : null);
  const gewaehlt = wahlFuer(buendel[aktiv]);

  function aktivieren(i: number, rollen = false) {
    const b = buendel[i];
    if (!b) return;
    setAktivId(b.ids[0]);
    if (rollen) karten.current[b.ids[0]]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  return (
    <>
      {erledigt.length > 0 && (
        <ul className="fo-erledigt" role="status">
          {erledigt.slice(-4).map((e, i) => (
            <li key={`${e.projekt_id}-${i}`}>✓ {e.text} — <Link to={`/projekte/${e.projekt_id}`}>ansehen</Link></li>
          ))}
        </ul>
      )}
      <div className="fo-zuordnen-raster">
        <div className="fo-karte-rahmen">
          <FotoKarte
            projekte={projekte}
            buendel={buendel}
            aktiv={aktiv}
            gewaehlt={gewaehlt}
            onBuendel={(i) => aktivieren(i, true)}
            onProjekt={(p) => {
              const b = buendel[aktiv];
              if (b) setWahl((w) => ({ ...w, [b.ids[0]]: p.projekt_id }));
            }}
          />
          <p className="fo-karte-legende">
            <span><i className="is-foto" />offene Fotos</span>
            <span><i style={{ background: "#2f7d4f" }} />aktiv</span>
            <span><i style={{ background: "#c08a1e" }} />Anfrage</span>
            <span><i style={{ background: "#2d6cb5" }} />Rechnung</span>
            <span><i style={{ background: "#6b6f6a" }} />abgeschlossen</span>
            {projekteLaden && <span>Projekte werden geladen …</span>}
          </p>
        </div>
        <div className="fo-buendel-liste">
          {buendel.map((b, i) => (
            <BuendelKarte
              key={b.ids[0]}
              ref={(el) => { karten.current[b.ids[0]] = el; }}
              b={b}
              aktiv={i === aktiv}
              projekte={projekte}
              gewaehltId={wahlFuer(b)}
              onAktiv={() => aktivieren(i)}
              onWahl={(id) => setWahl((w) => ({ ...w, [b.ids[0]]: id ?? "" }))}
              onErledigt={(e) => setErledigt((l) => [...l, e])}
            />
          ))}
        </div>
      </div>
    </>
  );
}

type BuendelProps = {
  b: StapelBuendel;
  aktiv: boolean;
  projekte: ZuordnenProjekt[];
  gewaehltId: string | null;
  onAktiv: () => void;
  onWahl: (id: string | null) => void;
  onErledigt: (e: Erledigt) => void;
};


const BuendelKarte = forwardRef<HTMLElement, BuendelProps>(function BuendelKarte(
  { b, aktiv, projekte, gewaehltId, onAktiv, onWahl, onErledigt }, ref,
) {
  const zuordnen = useStapelZuordnen();
  const verwerfen = useStapelVerwerfen();
  const [aus, setAus] = useState<Set<string>>(new Set());
  const [phase, setPhase] = useState<FotoPhase | null>(null);
  const [sicher, setSicher] = useState(false);
  const [schritt, setSchritt] = useState<Schritt | null>(null);
  const gewaehlt = projekte.find((p) => p.projekt_id === gewaehltId) ?? null;
  const mitte = b.breite != null && b.laenge != null ? { breite: b.breite, laenge: b.laenge } : null;
  const vorschlag = gewaehlt ? phaseVorschlag(gewaehlt.tage, b.tag) : null;
  const phaseJetzt = phase ?? vorschlag;
  const auswahl = b.ids.filter((id) => !aus.has(id));
  const fotos = b.fotos?.length ? b.fotos : b.ids.map((id) => ({ id, aufgenommen_am: "", breite: null, laenge: null }));

  // Neues Projekt → Phase wieder vorschlagen lassen.
  useEffect(() => { setPhase(null); }, [gewaehltId]);

  const abstand = gewaehlt && mitte && gewaehlt.breite != null && gewaehlt.laenge != null
    ? meter(mitte.breite, mitte.laenge, gewaehlt.breite, gewaehlt.laenge) : null;

  return (
    <article
      ref={ref}
      className={`fo-buendel${aktiv ? " is-aktiv" : ""}`}
      onClick={onAktiv}
      onFocusCapture={onAktiv}
    >
      <div className="fo-buendel-kopf">
        <strong>{tagLabel(b.tag)}</strong>
        <span>{b.anzahl} {b.anzahl === 1 ? "Foto" : "Fotos"}</span>
        {!mitte && <span className="fo-fehler">ohne Ort</span>}
      </div>
      <div className="fo-vorschau">
        {fotos.slice(0, 12).map((f) => {
          const drin = !aus.has(f.id);
          return (
            <button
              key={f.id}
              type="button"
              className={`fo-bild${drin ? "" : " ist-aus"}`}
              aria-pressed={drin}
              title={drin ? "Antippen, um dieses Foto auszunehmen" : "Antippen, um es wieder mitzunehmen"}
              onClick={(e) => {
                e.stopPropagation();
                onAktiv();
                setAus((s) => { const n = new Set(s); if (n.has(f.id)) n.delete(f.id); else n.add(f.id); return n; });
              }}
            >
              <img src={`/api/kontor/fotos/zuordnen/${f.id}/vorschau`} alt="" loading="lazy" />
              <i aria-hidden="true">{drin ? "✓" : ""}</i>
            </button>
          );
        })}
        {fotos.length > 12 && <span className="fo-mehr">+{fotos.length - 12}</span>}
      </div>
      {aus.size > 0 && <small className="fo-text">{auswahl.length} von {b.anzahl} ausgewählt — die übrigen bleiben im Stapel.</small>}

      <div className="fo-feld-gruppe">
        <span className="fo-feld-titel">Projekt</span>
        {b.erkannt && gewaehlt?.projekt_id === b.erkannt.projekt_id && (
          <span className="fo-vorschlag-hinweis">Vorschlag von Kontor — Baustelle im Umkreis von 150 m. Bitte prüfen und bestätigen.</span>
        )}
        <ProjektSuche projekte={projekte} mitte={mitte} gewaehlt={gewaehlt} onWahl={(p) => onWahl(p?.projekt_id ?? null)} />
        {!gewaehlt && b.vorschlaege.length > 0 && (
          <div className="fo-vorschlaege">
            {b.vorschlaege.slice(0, 3).map((v) => (
              <button key={v.projekt_id} type="button" className="vg-btn" onClick={(e) => { e.stopPropagation(); onWahl(v.projekt_id); }}>
                {v.titel} · {entfernung(v.meter)}
              </button>
            ))}
          </div>
        )}
        {!gewaehlt && <small className="fo-text">…oder auf der Karte ein Projekt antippen.</small>}
      </div>

      {gewaehlt && phaseJetzt && (
        <div className="fo-feld-gruppe">
          <span className="fo-feld-titel">Phase</span>
          <div className="fo-phasen" role="radiogroup" aria-label="Phase">
            {PHASEN.map((p) => (
              <button
                key={p.wert}
                type="button"
                role="radio"
                aria-checked={phaseJetzt === p.wert}
                className={phaseJetzt === p.wert ? "is-an" : ""}
                onClick={(e) => { e.stopPropagation(); setPhase(p.wert); }}
              >
                {p.text}
              </button>
            ))}
          </div>
          <small className="fo-text">
            {phase && phase !== vorschlag
              ? `Vorschlag aus den Terminen war „${PHASE_TEXT[vorschlag!]}“.`
              : gewaehlt.tage.length
                ? `Vorschlag aus den Terminen (${gewaehlt.tage.length === 1 ? kurzTag(gewaehlt.tage[0]) : `${kurzTag(gewaehlt.tage[0])}–${kurzTag(gewaehlt.tage[gewaehlt.tage.length - 1])}`}).`
                : "Das Projekt hat noch keine Termine — daher „Vorher“."}
            {abstand != null && abstand > 300 ? ` Achtung: die Baustelle liegt ${entfernung(abstand)} entfernt.` : ""}
          </small>
        </div>
      )}

      {schritt && <Arbeitsfortschritt s={schritt} was="zugeordnet" />}
      <div className="fo-buendel-aktionen">
        <button
          type="button"
          className="vg-btn vg-btn-primary"
          disabled={!gewaehlt || !auswahl.length || zuordnen.isPending}
          onClick={(e) => {
            e.stopPropagation();
            if (!gewaehlt || !phaseJetzt) return;
            zuordnen.mutate(
              { ids: auswahl, projekt_id: gewaehlt.projekt_id, phase: phaseJetzt, fortschritt: setSchritt },
              {
                onSuccess: (r) => onErledigt({
                  text: `${r.zugeordnet} ${r.zugeordnet === 1 ? "Foto" : "Fotos"} → ${gewaehlt.titel} (${PHASE_TEXT[phaseJetzt]})`,
                  projekt_id: gewaehlt.projekt_id,
                }),
                onSettled: () => setSchritt(null),
              },
            );
          }}
        >
          {zuordnen.isPending
            ? `Ordnet zu … ${schritt ? `${schritt.fertig}/${schritt.gesamt}` : ""}`
            : `${auswahl.length === b.anzahl && b.anzahl > 1 ? "Alle " : ""}${auswahl.length} ${b.erkannt && gewaehlt?.projekt_id === b.erkannt.projekt_id ? "bestätigen" : "zuordnen"}`}
        </button>
        <button
          type="button"
          className="vg-btn"
          disabled={!auswahl.length || verwerfen.isPending || zuordnen.isPending}
          onClick={(e) => {
            e.stopPropagation();
            if (!sicher) { setSicher(true); return; }
            verwerfen.mutate(auswahl, { onSettled: () => setSicher(false) });
          }}
          onBlur={() => setSicher(false)}
        >
          {sicher ? `Wirklich ${auswahl.length} verwerfen?` : "Verwerfen"}
        </button>
      </div>
      {zuordnen.isError && <p className="fo-fehler">{fehlerText(zuordnen.error)}</p>}
      {verwerfen.isError && <p className="fo-fehler">{fehlerText(verwerfen.error)}</p>}
    </article>
  );
});
